import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import { DateTime } from 'luxon';

import { nBusinessDaysFromStartDate } from 'helpers/time';

import { media } from 'context';
import { APP_BANNER_HEIGHT, DatePicker, HEADER_Z_INDEX, IconButton, Layout, PageLayout, PillButton, Text } from 'ds';
import { CLEANING_SERVICE_ONBOARDING_CHECKOUT_PATH, CLEANING_SERVICE_PATH } from 'routes';
import { actions } from 'store/CleaningService';
import {
  selectHasCleaningService,
  selectOnboardingServiceStartDate,
  selectOnboardingStandardDay
} from 'store/CleaningService/selectors';
import { actions as tenancyActions } from 'store/Tenancy';
import {
  selectActiveBuildingUnitTenancies,
  selectBuildingUnitTenancies,
  selectFirstUpcomingTenancyFromNow
} from 'store/Tenancy/selectors';
import { selectUIState } from 'store/UI/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectTenancyId } from 'store/v2/User/selectors';
import StickyHeader from 'ux/Layouts/Shared/StickyHeader';
import { getBuildingUnitTenancies } from 'ux/Tenant/TenantAdmin/requests';

import CleaningServiceRepeatCard from './CleaningServiceRepeatCard';
import CleaningServiceSelectDayCard from './CleaningServiceSelectDayCard';
import CleaningServiceCompareModal from '../CleaningServiceCompareModal';
import { getCleaningServicePricing, getCleaningServiceSchedules } from '../requests';

interface Props {}

const CleaningServiceSetUp: React.FC<Props> = () => {
  const { appBannerType } = useAppSelector(selectUIState);
  const buildingUnitTenancies = useAppSelector(selectBuildingUnitTenancies);
  const activeBuildingUnitTenancies = useAppSelector(selectActiveBuildingUnitTenancies);
  const firstUpcomingTenancyFromNow = useAppSelector(selectFirstUpcomingTenancyFromNow);
  const hasCleaningService = useAppSelector(selectHasCleaningService);
  const standardDayValue = useAppSelector(selectOnboardingStandardDay);

  const selectedStartDate = useAppSelector(selectOnboardingServiceStartDate);
  const { isMobile } = useContext(media);
  const dispatch = useAppDispatch();
  const history = useHistory();

  const tenancyId = useAppSelector(selectTenancyId);
  const [modalIsVisible, setModalIsVisible] = useState(false);

  useEffect(() => {
    dispatch(actions.setOnboardingTouchUpDay(null));
  }, [dispatch]);

  useEffect(() => {
    if (!tenancyId) return;
    Promise.all([
      getCleaningServicePricing(),
      getCleaningServiceSchedules(),
      getBuildingUnitTenancies({ tenancyId })
    ]).then(([pricing, schedules, buildingUnitTenanciesResponse]) => {
      dispatch(actions.setPricing(pricing.data));
      dispatch(actions.setSchedules(schedules.data));
      dispatch(tenancyActions.setBuildingUnitTenancies(buildingUnitTenanciesResponse.data));
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const excludeDates = useMemo(() => {
    if (!standardDayValue) return [];

    const fiveMonthsFromNow = DateTime.now().plus({ months: 5 });
    let currentDay = DateTime.now();
    const datesToExclude = [];

    while (currentDay <= fiveMonthsFromNow) {
      const isStandardDay = currentDay.weekday === standardDayValue;

      if (!isStandardDay) {
        datesToExclude.push(currentDay.toJSDate());
      }

      currentDay = currentDay.plus({ days: 1 });
    }

    return datesToExclude;
  }, [standardDayValue]);

  if (!buildingUnitTenancies || !tenancyId) return null;

  const threeBusinessDaysFromNow = nBusinessDaysFromStartDate({ startDate: DateTime.now().startOf('day'), nDays: 3 });

  const minDate = (): DateTime => {
    if (
      (activeBuildingUnitTenancies?.length && activeBuildingUnitTenancies.length > 0) ||
      !firstUpcomingTenancyFromNow
    ) {
      return threeBusinessDaysFromNow;
    }

    const upcomingTenancyStartDate = DateTime.fromISO(firstUpcomingTenancyFromNow.start_date);

    return DateTime.max(threeBusinessDaysFromNow, upcomingTenancyStartDate);
  };

  if (hasCleaningService) {
    return <Redirect to={`${CLEANING_SERVICE_PATH}`} />;
  }

  const setupDescription = (
    <Layout direction="column" gap={8}>
      <Text size="body-sm" color="gray-700">
        Please select at least one weekly standard cleaning to activate your service.
      </Text>
      <Text size="body-sm" color="gray-700" semibold>
        You'll be able to make changes to your schedule once you've initiated the service.
      </Text>
    </Layout>
  );

  const standardDayCard = (
    <CleaningServiceSelectDayCard
      standardDayValue={standardDayValue}
      touchUpDayValue={null}
      cleaningType="standard"
      menuPosition="bottom"
      onSelect={({ value }) => dispatch(actions.setOnboardingStandardDay(value))}
      value={standardDayValue}
      setModalIsVisible={setModalIsVisible}
    />
  );

  const startDateSection = (
    <Layout direction="column" gap={24}>
      <Layout direction="column" gap={8}>
        <Text size="headline-sm">Service start date</Text>
        <Text size="body-sm" color="gray-700">
          You can always skip cleanings and evolve your schedule later.
        </Text>
      </Layout>
      <Layout direction="column" gap={8}>
        <DatePicker
          excludeDates={excludeDates}
          size="sm"
          selected={selectedStartDate}
          onChange={date => {
            if (date) {
              dispatch(actions.setOnboardingServiceStartDate(date.toISOString()));
            } else {
              dispatch(actions.setOnboardingServiceStartDate(null));
            }
          }}
          minDate={minDate().toJSDate()}
          maxDate={excludeDates[excludeDates.length - 1]}
          onlyWeekdays={true}
          disabled={!selectedStartDate && standardDayValue === null}
        />
        <CleaningServiceRepeatCard cleaningType="standard" />
      </Layout>
    </Layout>
  );

  if (isMobile) {
    return (
      <Layout paddingBottom={100} color="white">
        <StickyHeader
          position="fixed"
          top={appBannerType ? APP_BANNER_HEIGHT : 0}
          zIndex={HEADER_Z_INDEX}
          shadow={false}
          transition="background 200ms"
          height={60}
          align="center"
          width="100%"
        >
          <Layout paddingRight={60} paddingLeft={12} direction="row" align="center" width="100%">
            <IconButton
              name={'leftChevron'}
              size="md"
              iconSize={28}
              type="noBackground"
              onClick={() => {
                history.push(CLEANING_SERVICE_PATH);
              }}
            />
            <Layout width="100%">
              <Text size="headline-sm" align="center">
                Build your schedule
              </Text>
            </Layout>
          </Layout>
        </StickyHeader>
        <Layout flex width="100%" direction="column" gap={32} paddingX={24} marginTop={60} paddingTop={24}>
          <Layout direction="column" gap={16}>
            <Text size="headline-sm">Select your cleaning service</Text>
            {setupDescription}
            {standardDayCard}
          </Layout>
          <Layout minHeight={8} color="gray-25" position="relative" left={-24} width="calc(100% + 48px)" />
          {startDateSection}
        </Layout>
        <Layout
          direction="column"
          gap={12}
          align="center"
          position="fixed"
          bottom={0}
          color="white"
          paddingX={24}
          paddingY={12}
          width="100vw"
          zIndex={4}
        >
          <PillButton
            text="Go to checkout"
            fullWidth
            type="purpleGradient"
            size="lg"
            borderRadius={40}
            onClick={() => {
              history.push(CLEANING_SERVICE_ONBOARDING_CHECKOUT_PATH);
            }}
            disabled={!selectedStartDate || standardDayValue === null}
          />
          <Text size="body-xs" color="gray-400">
            Service begins after checkout
          </Text>
        </Layout>
        <CleaningServiceCompareModal
          isVisible={modalIsVisible}
          setIsVisible={setModalIsVisible}
          useCalendarLink={false}
        />
      </Layout>
    );
  }

  return (
    <PageLayout
      type="default"
      backLink={{ href: CLEANING_SERVICE_PATH }}
      title=" "
      maxWidth={804}
      hasBorderBottom
      navBarTopOffset={appBannerType ? APP_BANNER_HEIGHT : 0}
    >
      <Layout flex width="100%" direction="column" gap={32}>
        <Text size="headline-lg">Build your schedule</Text>
        <Layout direction="column" gap={16}>
          <Text size="headline-sm">Select your cleaning service</Text>
          {setupDescription}
          {standardDayCard}
        </Layout>
        {startDateSection}
        <Layout direction="row" gap={12} align="center">
          <Layout width={200}>
            <PillButton
              text="Go to checkout"
              fullWidth
              type="purpleGradient"
              size="lg"
              onClick={() => {
                history.push(CLEANING_SERVICE_ONBOARDING_CHECKOUT_PATH);
              }}
              disabled={!selectedStartDate || standardDayValue === null}
            />
          </Layout>
          <Text size="body-xs" color="gray-400">
            Service begins after checkout
          </Text>
        </Layout>
      </Layout>
      <CleaningServiceCompareModal
        isVisible={modalIsVisible}
        setIsVisible={setModalIsVisible}
        useCalendarLink={false}
      />
    </PageLayout>
  );
};

export default CleaningServiceSetUp;
